<script setup lang="ts">
import { computed } from "vue";

import { metric_help } from "@/assets/i18n/en.json";
import FloatingTooltip from "@/components/ui/FloatingTooltip.vue";
import MarkdownWidget from "@/components/ui/MarkdownWidget.vue";
import type { MetricDto } from "@/dto";

const { metric } = defineProps<{ metric: MetricDto }>();

const hasHelp = computed(() => metric.name in metric_help);
const help = computed(() => {
  const i18n: { math: string; description: string } | undefined = (metric_help as any)[metric.name];
  return { math: i18n?.math ?? "", description: i18n?.description ?? "" };
});
</script>

<template>
  <FloatingTooltip v-if="hasHelp" class="metric-tooltip" strategy="absolute">
    <div class="metric-name">
      {{ metric.verbose_name }}
    </div>
    <template #tooltipContent>
      <div class="metric-tooltip-content">
        <div v-if="help.math.length > 0" class="math">
          <MarkdownWidget :source="help.math" :use-highlight="false" />
        </div>
        <div class="text">
          <div class="title">Why it matters:</div>
          <div class="description">
            <MarkdownWidget :source="help.description" :use-highlight="false" />
          </div>
        </div>
      </div>
    </template>
  </FloatingTooltip>
  <div v-else class="metric-name">
    {{ metric.verbose_name }}
  </div>
</template>

<style scoped>
.metric-tooltip {
  cursor: help;
}

.metric-name {
  color: var(--color-text-secondary);
  text-decoration: underline dashed;
  text-underline-offset: var(--spacing-4);
}

.metric-tooltip-content {
  display: flex;
  flex-direction: row;
  gap: var(--spacing-12);

  .math {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--spacing-4);
    border-radius: var(--radius-s);
    background-color: #242428;
    color: var(--color-text-disable);
  }

  .title {
    color: var(--color-text-disable);
  }

  .description {
    color: var(--color-text-button-primary);
  }
}
</style>
